import React, { useEffect, useState } from "react";
import { Button, Col, InputMask, Link, Modal, Row } from "@mtm/components";
import { notification, Input } from "antd";
import "antd/dist/antd.css";
import { ReCaptcha } from "../ReCaptcha/ReCaptcha";
import {
  GeneralDefinition,
  RegistrationComponentLoad,
  RegistrationStatusDefaultValues,
  TrackAnalytics,
  ZipCodeAddress,
  defaultZipCodeAddress,
} from "../../types/ApplicationTypes";
import {
  getGeocode,
  ZipCodeSearchUpsert,
  getWandaOrganizationByName,
} from "../../services/RegistrationService";
import ZipImage from "../../assets/images/Wanda_ZipImage.png";
import x_red from "../../assets/images/x_red.svg";
import checkmark_green from "../../assets/images/checkmark_green.svg";
import CrossMark from "../../assets/images/CrossMark.png";
import Styles from "./Zipcode.module.scss";
import { config } from "../../types/CommonEnv";
import { IWandaServiceArea } from "../../types/TripTypes";
import getWandaServiceAreaHelper from "../../helpers/tripHelper";

type OrgAttributesResponse = {
  attributeName: string;
  attributeValue: string;
  id: number;
  organizationId: number;
};
type Props = {
  setPageType: Function;
  setZipData: Function;
  setWandaOrgId: Function;
};

enum ErrorMessage {
  "Has_No_Error" = "",
  "Invalid_ZipCode" = "Invalid Zip Code",
}

const Zipcode: React.FC<Props> = ({
  setPageType,
  setZipData,
  setWandaOrgId,
}) => {
  const [zipcode, setZipcode] = useState<number>(0);
  const [isValidZipcode, setIsValidZipcode] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isAreaInService, setIsAreaInService] = useState<boolean>(true);
  const [isContinueButtonClicked, setIsContinueButtonClicked] = useState<
    boolean
  >(false);
  const [noSeriveArea, setNoServiceArea] = useState<boolean>(false);
  const [zipButtonEnabled, setZipButtonEnabled] = useState<boolean>(false);
  const attributeNames = ["CCPCoverageArea"];
  const [address, setAddress] = useState<ZipCodeAddress>(defaultZipCodeAddress);

  async function handleZipcodeChange(
    e: any,
    executeReCaptcha: () => Promise<string>
  ) {
    const zipCodeValue = Number(e.target.value);
    setIsValidZipcode(false);
    setZipcode(zipCodeValue);
    setZipButtonEnabled(false);
    setAddress(defaultZipCodeAddress);

    if (e.target.value.length === 5) {
      const token = await executeReCaptcha();

      const validZipCode =
        zipCodeValue.toString().length === 5
          ? zipCodeValue.toString()
          : zipCodeValue.toString().padStart(5, "0");

      try {
        const data = {
          zip: validZipCode,
          recaptchaToken: token,
        };
        const geoCodeAddress = await getGeocode(data);
        if (geoCodeAddress.length !== 0) {
          setAddress({
            city: geoCodeAddress[0].city,
            state: geoCodeAddress[0].state,
          });
          validateZipcode(geoCodeAddress[0].city, zipCodeValue);
          setZipButtonEnabled(true);
        } else {
          setAddress(defaultZipCodeAddress);
          validateZipcode("", zipCodeValue);
        }
      } catch (error) {
        notification.error({
          message: "Error",
          description: "Failed to fetch Address information.",
        });
      }
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (/^[1-9]$|^0$/.test(e.key)) {
    } else {
      e.preventDefault();
    }
  };

  async function handleSignUp(executeReCaptcha: () => Promise<string>) {
    try {
      const token = await executeReCaptcha();
      await ZipCodeSearchUpsert({
        zipCode: zipcode,
        signUpCountFlag: "Y",
        recaptchaToken: token,
      });
      window.location.href = "https://wanda.care/service-notification/";
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to update zipcode signup count.",
      });
    }
  }

  async function handleContinueClicked(
    executeReCaptcha: () => Promise<string>
  ) {
    setIsContinueButtonClicked(true);
    validateZipcode(address.city, zipcode);
    const recaptchaToken = await executeReCaptcha();
    const orgName = await getWandaOrganizationByName(
      config.organizationName,
      RegistrationStatusDefaultValues.DefaultPersonId,
      recaptchaToken
    );
    setWandaOrgId(orgName && orgName?.benefitGroupId);
    if (isValidZipcode) {
      try {
        const token = await executeReCaptcha();
        const serviceArea: IWandaServiceArea[] = await getWandaServiceAreaHelper(
          zipcode.toString(),
          token
        );
        let availableZipcodes: boolean = true;
        if (serviceArea?.length !== 0 && isValidZipcode) {
          availableZipcodes = serviceArea?.length !== 0;
          setIsAreaInService(availableZipcodes);
          setNoServiceArea(true);
          if (!availableZipcodes) {
            const token = await executeReCaptcha();
            await ZipCodeSearchUpsert({
              zipCode: zipcode,
              lookupCountFlag: "Y",
              recaptchaToken: token,
            });
          }
        } else {
          TrackAnalytics(
            "/Registration",
            "Valid Zipcode - No Services Offered",
            "Valid Email"
          );
          availableZipcodes = false;
          setIsAreaInService(false);
          setNoServiceArea(true);
        }
        if (isValidZipcode && availableZipcodes) {
          const data = {
            city: address.city,
            zipcode: zipcode,
            state: address.state,
          };
          setZipData(data);
          setPageType(RegistrationComponentLoad.Email);
        }
      } catch (error) {
        notification.error({
          message: "Error",
          description: "Failed to fetch service Area.",
        });
      }
    }
  }

  function validateZipcode(res: string, paramZipCode: number) {
    if (res === null || res === "") {
      setErrorMessage(ErrorMessage.Invalid_ZipCode);
    } else {
      setErrorMessage(ErrorMessage.Has_No_Error);
      setIsValidZipcode(true);
    }
  }

  function handleNoServiceAreaSuccess() {
    setNoServiceArea(false);
  }
  function handleNoServiceAreaCancel() {
    setNoServiceArea(false);
    setIsAreaInService(true);
  }
  function handleTryAnotherZipcode() {
    setIsAreaInService(true);
    setNoServiceArea(false);
    setAddress(defaultZipCodeAddress);
    setZipcode(0);
    setIsValidZipcode(false);
  }

  function useWindowSize() {
    const [size, setSize] = useState([window.innerWidth]);
    useEffect(() => {
      const handleResize = () => {
        setSize([window.innerWidth]);
      };
      window.addEventListener("resize", handleResize);
    }, []);
    return size;
  }

  const [width] = useWindowSize();

  return (
    <>
      {
        <div>
          {
            <ReCaptcha>
              {(captcha) =>
                isAreaInService || width > GeneralDefinition.MobileView ? (
                  <div className={Styles.zipMargin}>
                    <Row className={Styles.zipPosition} gutter={[0, 100]}>
                      <Col xs={24} lg={0} />
                      <Col span={2} />
                      <Col xs={0} lg={10}>
                        <div className={Styles.zipImage}>
                          <img
                            src={ZipImage}
                            alt="ZipImage"
                            className={Styles.imageWidth}
                          />
                        </div>
                      </Col>
                      <Col span={20} lg={10}>
                        <Row className={Styles.zipCodeArea}>
                          <Col span={24}>
                            <div className={Styles.zipSpan}>
                              Where do you need services?
                            </div>
                          </Col>
                          <Col span={24}>
                            <div className={"subTextFont " + Styles.zipLabel}>
                              Enter the zip code where you need services to see
                              if Wanda can work her magic in your area!
                            </div>
                          </Col>
                          <Col span={24}>
                            <Row className={Styles.zipWrap}>
                              <Col
                                className={"subTextFont " + Styles.zipCodeText}
                              >
                                <label>5-Digit Zip Code</label>
                              </Col>
                              <Col>
                                <Input
                                  placeholder="12345"
                                  className="mg1 subTextFont"
                                  onChange={(e) =>
                                    handleZipcodeChange(e, captcha.execute)
                                  }
                                  onKeyPress={handleKeyPress}
                                  maxLength={5}
                                />
                              </Col>
                            </Row>
                            <Row>
                              <Col span={8} className={Styles.zipInfoGap}></Col>
                              <Col span={12} className={Styles.zipInfo}>
                                {errorMessage !== "" ? (
                                  <div
                                    className={"subTextFont " + Styles.zipError}
                                  >
                                    <img
                                      src={x_red}
                                      alt="x_red"
                                      className={Styles.zipIcon}
                                    ></img>
                                    &nbsp; {errorMessage}
                                  </div>
                                ) : (
                                  <div>
                                    {address.city !== "" &&
                                    address.state !== "" ? (
                                      <div
                                        className={
                                          "subTextFont " + Styles.zipIndication
                                        }
                                      >
                                        <img
                                          src={checkmark_green}
                                          alt="checkmark_green"
                                          className={Styles.zipIcon}
                                        ></img>
                                        &nbsp; {address.city}, {address.state}
                                      </div>
                                    ) : (
                                      <div>&nbsp;</div>
                                    )}
                                  </div>
                                )}
                              </Col>
                            </Row>
                          </Col>
                          <Col span={24} lg={20}>
                            <Row justify="center">
                              <div className={Styles.zipButtonPadding}>
                                <Button
                                  className={
                                    address.city === ""
                                      ? "zipButtonDisabled"
                                      : "zipButton"
                                  }
                                  name="zipButton"
                                  clickHandler={(e) =>
                                    handleContinueClicked(captcha.execute)
                                  }
                                  disabled={!zipButtonEnabled}
                                >
                                  Continue
                                </Button>
                              </div>
                            </Row>
                          </Col>
                          <Col span={24} lg={20}>
                            <Row justify="center">
                              <div className={"subTextFont " + Styles.zipLink}>
                                <Link href="/">
                                  {" "}
                                  Already have an account? Login here
                                </Link>
                              </div>
                            </Row>
                          </Col>
                        </Row>
                      </Col>
                      <Col span={2} />
                    </Row>
                    <div>
                      <Modal
                        handleSuccess={handleNoServiceAreaSuccess}
                        handleCancel={handleNoServiceAreaCancel}
                        visible={noSeriveArea}
                        okButtonProps={{ hidden: true }}
                        cancelButtonProps={{ hidden: true }}
                        width={547}
                        centered={true}
                      >
                        <div className={Styles.zipModal}>
                          <img
                            src={CrossMark}
                            alt="Crossmark"
                            className="modalPopUpCloseIcon"
                            onClick={handleNoServiceAreaCancel}
                          />
                          <div>
                            We’re sorry, but Wanda isn’t available in your area
                            yet:
                          </div>
                          <h2 className="subTextFont">
                            {address.city}, {address.state} {zipcode.toString().padStart(5, '0')}
                          </h2>
                          <p className="subTextFont">
                            Would you like us to let you know when we’re in your
                            area?
                          </p>
                        </div>
                        <div className={Styles.zipModalButtonAlign}>
                          <div className="zipModalButton">
                            <Button
                              className="cancelBtn"
                              key="close"
                              clickHandler={handleNoServiceAreaCancel}
                            >
                              No Thanks
                            </Button>
                          </div>
                          <div className="zipModalButtonSignup">
                            <Button
                              className="saveBtn"
                              key="close"
                              clickHandler={(e) =>
                                handleSignUp(captcha.execute)
                              }
                            >
                              Sure
                            </Button>
                          </div>
                        </div>
                      </Modal>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className={Styles.zipPosition}>
                      {isContinueButtonClicked && (
                        <Row justify="center" className={Styles.zipRow}>
                          <Col>
                            <Col className={Styles.zipNSLabel2}>
                              We’re sorry, but Wanda isn’t available in your
                              area yet:
                            </Col>
                            <Col
                              className={"subTextFont " + Styles.zipNSLabel3}
                            >
                              {address.city}, {address.state} {zipcode.toString().padStart(5, '0')}
                            </Col>
                            <Col
                              className={"subTextFont " + Styles.zipNSLabel1}
                            >
                              Would you like us to let you know when we’re in
                              your area?
                            </Col>
                            <Col>
                              <div>
                                <Button
                                  className={Styles.zipButtonSignup}
                                  clickHandler={(e) =>
                                    handleSignUp(captcha.execute)
                                  }
                                >
                                  Sure
                                </Button>
                              </div>
                              <div>
                                <Button
                                  className={Styles.zipButtonClose}
                                  clickHandler={handleTryAnotherZipcode}
                                >
                                  No Thanks
                                </Button>
                              </div>
                            </Col>
                          </Col>
                        </Row>
                      )}
                    </div>
                  </div>
                )
              }
            </ReCaptcha>
          }
        </div>
      }
    </>
  );
};
export default Zipcode;
